import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronRight, Lock, Home } from "lucide-react";
import { api } from "../../lib/api";
import { programFor, flattenItems } from "./programmeStructure";
import { ProgIcon } from "../../components/ProgIcon";
import { StickyBack } from "../../components/StickyBack";

function PhaseRoadmap({ groups, locked, active, setActive }) {
  const tab = (on) => on ? "bg-bordeaux font-medium text-white shadow-[0_0_20px_rgba(123,17,58,0.6)]" : "text-white/70 hover:bg-white/5 hover:text-white";
  const base = "flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-[13px] transition-colors duration-200";
  return (
    <div data-testid="phase-roadmap" className="flex w-full gap-1 overflow-x-auto rounded-full bg-[#2D181E] p-1.5">
      <button data-testid="roadmap-home" onClick={() => setActive(-1)} className={`${base} ${tab(active === -1)}`}><Home size={14} /> Accueil</button>
      {groups.map((g, i) => {
        const isLocked = flattenItems(g.items).every((x) => locked.includes(x.node.slug));
        const click = () => isLocked ? toast("Cette étape sera débloquée bientôt par Fanny") : setActive(i);
        return (
          <button key={i} data-testid={`roadmap-step-${i}`} data-locked={isLocked} onClick={click} className={`${base} flex-1 ${isLocked ? "cursor-not-allowed text-white/30" : tab(active === i)}`}>
            {isLocked && <Lock size={13} />}{g.items[0].label}
          </button>
        );
      })}
    </div>
  );
}

// 👉 Fanny : lien d'intégration de ta vidéo d'onboarding.
const ONBOARDING_VIDEO = "https://www.youtube.com/embed/iHzEqakdahE";

function StepRow({ item, locked, navigate }) {
  const open = () => {
    if (locked) { toast("🔒 Cette ressource sera débloquée bientôt par Fanny"); return; }
    if (item.slug) navigate(item.to || `/espace/programme/${item.slug}`);
    else toast("Cette page sera bientôt disponible ✨");
  };
  return (
    <button
      onClick={open}
      data-testid={`programme-item-${item.slug}`}
      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
        locked ? "cursor-default border-line bg-cream/60 opacity-50" : "border-line bg-white hover:border-bordeaux-border hover:bg-bordeaux-tint/30"
      }`}
    >
      <ProgIcon slug={item.slug} size="sm" locked={locked} />
      <span className={`flex-1 text-sm font-medium ${locked ? "text-ink-muted" : "text-ink"}`}>{item.label}</span>
      {locked ? <Lock size={15} className="text-ink-muted" /> : <ChevronRight size={15} className="text-ink-muted" />}
    </button>
  );
}

export default function ClientPhase() {
  const { phase } = useParams();
  const navigate = useNavigate();
  const { customProg } = useOutletContext() || {};
  const isPhase1 = phase === "1";
  const audit = isPhase1 && customProg;
  const [locked, setLocked] = useState([]);
  const [active, setActive] = useState(-1);
  const home = !isPhase1 || audit || active === -1;

  useEffect(() => {
    api.get("/client/access").then(({ data }) => setLocked(data.locked_resources || [])).catch(() => {});
  }, []);

  const phaseData = programFor(customProg).find((p) => String(p.phase) === phase);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl space-y-8">
      <StickyBack onClick={() => navigate("/espace/parcours")}>Le programme</StickyBack>

      <div>
        <p className="eyebrow">Phase {phase}</p>
        <h1 className="mt-2 font-sans text-3xl font-medium text-ink sm:text-4xl">
          {audit ? "Audit et restructuration de ton business" : isPhase1 ? "Mise en place & Stratégie" : "Exécution & Optimisation"}
        </h1>
      </div>

      {isPhase1 && !audit && phaseData && <PhaseRoadmap groups={phaseData.groups} locked={locked} active={active} setActive={setActive} />}

      {home && (audit ? (
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="eyebrow">Consignes</p>
          <p className="mt-2 font-light leading-relaxed text-ink-soft">
            Complète les trois pages ci-dessous dans l'ordre. À la fin de chaque page, clique sur le bouton pour me prévenir 👇🏻
          </p>
        </div>
      ) : isPhase1 ? (
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="eyebrow">Consignes</p>
          <p className="mt-2 font-light leading-relaxed text-ink-soft">
            Prends le temps de regarder la vidéo d'onboarding ci-dessous. Je t'explique comment fonctionne
            l'accompagnement et comment utiliser ton espace membre 👇🏻
          </p>
          <p className="mt-3 font-light leading-relaxed text-ink-soft">
            Ensuite, tu n'as plus qu'à suivre les étapes dans l'ordre, en commençant par « Commence ici ».
          </p>
          <p className="mt-3 font-light leading-relaxed text-ink-soft">
            Chaque étape a son importance, alors prends bien le temps de terminer chacune d'elles avant de passer
            à la suivante !
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="eyebrow">Consignes</p>
          <p className="mt-2 font-light leading-relaxed text-ink-soft">
            Place à l'exécution. Chaque semaine, tu publies tes contenus, tu complètes ton tableau de suivi et je
            les analyse pour ajuster ta stratégie et accélérer tes résultats 👇🏻
          </p>
        </div>
      ))}

      {isPhase1 && !audit && home && (
        <div className="aspect-video overflow-hidden rounded-2xl border border-line bg-black">
          <iframe src={ONBOARDING_VIDEO} title="Vidéo d'onboarding" className="h-full w-full" allowFullScreen frameBorder="0" />
        </div>
      )}

      <div id="phase-steps" className="space-y-6">
        {phaseData?.groups.filter((_, i) => home || i === active).map((group, i) => (
          <motion.div key={`${active}-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="phase-group">
            {isPhase1 && !home && <p className="mb-1 text-lg font-medium text-bordeaux-dark">{group.items[0].label}</p>}
            {group.duration && <p className="text-sm font-light italic text-ink-muted">Durée : {group.duration}</p>}
            <div className="mt-2 space-y-2">
              {group.items.map((item) => (
                <StepRow key={item.slug} item={item} locked={locked.includes(item.slug)} navigate={navigate} />
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
